import { useCallback, useEffect, useMemo, useState } from 'react'
import { listPayments, type SubPayment } from '../../services/subscriptions'
import { deleteExpense, listExpenses, saveExpense, type Expense, type ExpenseKind } from '../../services/expenses'
import { activeIn, monthLabel, monthlyTotals } from '../../utils/finance'
import { dayKey } from '../../utils/subscription'
import { amountInput, formatTRY, kurusToLiraInput, liraToKurus } from '../../utils/money'
import { Spinner } from '../Spinner'
import { DateField } from '../PickerFields'
import { ConfirmDialog } from '../pos/ConfirmDialog'
import { PencilIcon, PlusIcon, RepeatIcon, TrashIcon, XIcon } from '../icons'
import './SubscriptionsPanel.css'
import './FinancePanel.css'

const fmtDay = (day: string) =>
  new Date(`${day}T12:00:00`).toLocaleDateString('tr-TR', { day: 'numeric', month: 'short', year: 'numeric' })

/**
 * Sistem Yönetimi > Gelir-Gider (2 Kas 2026, kullanıcı: "toplamda kaç para
 * gelirim olmuş, gider kalemleri ekleme, aylık abonelik girme olsun").
 * Gelir abonelik ödemelerinden (Abonelikler sekmesinde girilen), gider
 * burada: tek seferlik ya da her ay tekrar eden. Tutarlar TL.
 */
export function FinancePanel() {
  const [payments, setPayments] = useState<SubPayment[]>([])
  const [expenses, setExpenses] = useState<Expense[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  /** Açık gider penceresi: yeni (null id) ya da düzenlenen. */
  const [editing, setEditing] = useState<Partial<Expense> | null>(null)
  const [confirm, setConfirm] = useState<Expense | null>(null)
  const today = dayKey(new Date())
  const thisMonth = today.slice(0, 7)

  const reload = useCallback(async () => {
    setError(null)
    try {
      const [p, e] = await Promise.all([listPayments(), listExpenses()])
      setPayments(p)
      setExpenses(e)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Gelir-gider okunamadı.')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void reload()
  }, [reload])

  const months = useMemo(() => monthlyTotals(payments, expenses, thisMonth), [payments, expenses, thisMonth])
  const now = months[0]
  const total = months.reduce(
    (t, m) => ({ income: t.income + m.incomeKurus, expense: t.expense + m.expenseKurus }),
    { income: 0, expense: 0 },
  )
  const monthlyRun = expenses
    .filter((e) => e.kind === 'monthly' && activeIn(e, thisMonth))
    .reduce((s, e) => s + e.amountKurus, 0)

  const remove = async (e: Expense) => {
    setConfirm(null)
    try {
      await deleteExpense(e.id)
      await reload()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Silinemedi.')
    }
  }

  if (loading) return <Spinner label="Gelir-gider yükleniyor…" />

  const profit = now.incomeKurus - now.expenseKurus
  const totalProfit = total.income - total.expense

  return (
    <section className="superadmin__panel subs fin">
      <div className="superadmin__panel-head">
        <h2>Gelir-Gider</h2>
        <button
          type="button"
          className="btn btn--primary fin__add"
          onClick={() => setEditing({ kind: 'once', spentOn: today })}
        >
          <PlusIcon />
          Gider ekle
        </button>
      </div>

      {error && <p className="superadmin__error">{error}</p>}

      {/* Büyük rakam bu ay, altında toplam. */}
      <div className="subs__stats">
        <div className="subs__stat fin__stat is-in">
          <span>Gelir, {monthLabel(thisMonth)}</span>
          <b>{formatTRY(now.incomeKurus)}</b>
          <small>Toplam {formatTRY(total.income)}</small>
        </div>
        <div className="subs__stat fin__stat is-out">
          <span>Gider, {monthLabel(thisMonth)}</span>
          <b>{formatTRY(now.expenseKurus)}</b>
          <small>
            Toplam {formatTRY(total.expense)}
            {monthlyRun > 0 && `, her ay ${formatTRY(monthlyRun)}`}
          </small>
        </div>
        <div className={`subs__stat fin__stat ${profit < 0 ? 'is-neg' : 'is-pos'}`}>
          <span>Kar, {monthLabel(thisMonth)}</span>
          <b>{formatTRY(profit)}</b>
          <small>Toplam {formatTRY(totalProfit)}</small>
        </div>
      </div>

      <div className="fin__cols">
        <div className="fin__block">
          <h3>Ay ay</h3>
          <div className="ftable">
            <div className="ftable__wrap">
              <table>
                <thead>
                  <tr>
                    <th>Ay</th>
                    <th className="fin__num">Gelir</th>
                    <th className="fin__num">Gider</th>
                    <th className="fin__num">Kar</th>
                  </tr>
                </thead>
                <tbody>
                  {months.map((m) => {
                    const k = m.incomeKurus - m.expenseKurus
                    return (
                      <tr key={m.month}>
                        <td className="fin__month">{monthLabel(m.month)}</td>
                        <td className="fin__num is-in">{formatTRY(m.incomeKurus)}</td>
                        <td className="fin__num is-out">{formatTRY(m.expenseKurus)}</td>
                        <td className={`fin__num ${k < 0 ? 'is-neg' : ''}`}>
                          <b>{formatTRY(k)}</b>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <div className="fin__block fin__block--exp">
          <h3>Giderler</h3>
          {expenses.length === 0 ? (
            <p className="fin__empty">
              Henüz gider yok. Sunucu, mağaza hesabı, reklam gibi giderleri "Gider ekle" ile girin; her ay
              ödenenleri "Her ay" seçerek bir kez girmeniz yeter.
            </p>
          ) : (
            <div className="ftable">
              <div className="ftable__wrap">
                <table>
                  <thead>
                    <tr>
                      <th>Gider</th>
                      <th>Tarih</th>
                      <th className="fin__num">Tutar</th>
                      <th className="fin__act" aria-label="İşlem" />
                    </tr>
                  </thead>
                  <tbody>
                    {expenses.map((e) => {
                      const ended = e.kind === 'monthly' && !activeIn(e, thisMonth) && e.spentOn.slice(0, 7) <= thisMonth
                      return (
                        <tr key={e.id} className={ended ? 'is-ended' : ''}>
                          <td>
                            <span className="ttable__two">
                              <b>{e.title}</b>
                              <small>
                                {e.kind === 'monthly' && (
                                  <span className="fin__tag">
                                    <RepeatIcon />
                                    Her ay
                                  </span>
                                )}
                                {[e.category, e.note].filter(Boolean).join(', ')}
                              </small>
                            </span>
                          </td>
                          <td className="fin__date">
                            {e.kind === 'monthly'
                              ? e.endsOn
                                ? `${monthLabel(e.spentOn.slice(0, 7))} - ${monthLabel(e.endsOn.slice(0, 7))}`
                                : `${monthLabel(e.spentOn.slice(0, 7))}'dan beri`
                              : fmtDay(e.spentOn)}
                          </td>
                          <td className="fin__num">
                            <b>{formatTRY(e.amountKurus)}</b>
                            {e.kind === 'monthly' && <small> / ay</small>}
                          </td>
                          <td className="fin__act">
                            <button
                              type="button"
                              className="subs__icon-btn"
                              aria-label="Düzenle"
                              title="Düzenle"
                              onClick={() => setEditing(e)}
                            >
                              <PencilIcon />
                            </button>
                            <button
                              type="button"
                              className="subs__icon-btn is-danger"
                              aria-label="Sil"
                              title="Sil"
                              onClick={() => setConfirm(e)}
                            >
                              <TrashIcon />
                            </button>
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>

      {editing && (
        <ExpenseDialog
          initial={editing}
          today={today}
          onClose={() => setEditing(null)}
          onSaved={async () => {
            setEditing(null)
            await reload()
          }}
        />
      )}

      {confirm && (
        <ConfirmDialog
          title="Gider silinsin mi?"
          body={`${confirm.title}, ${formatTRY(confirm.amountKurus)}${confirm.kind === 'monthly' ? ' / ay. Geçmiş aylardan da düşer; yalnız bundan sonrası için durdurmak isterseniz bitiş tarihi girin.' : '.'}`}
          confirmLabel="Sil"
          danger
          onConfirm={() => void remove(confirm)}
          onClose={() => setConfirm(null)}
        />
      )}
    </section>
  )
}

function ExpenseDialog({
  initial,
  today,
  onClose,
  onSaved,
}: {
  initial: Partial<Expense>
  today: string
  onClose: () => void
  onSaved: () => Promise<void>
}) {
  const [title, setTitle] = useState(initial.title ?? '')
  const [category, setCategory] = useState(initial.category ?? '')
  const [amount, setAmount] = useState(initial.amountKurus != null ? kurusToLiraInput(initial.amountKurus) : '')
  const [kind, setKind] = useState<ExpenseKind>(initial.kind ?? 'once')
  const [spentOn, setSpentOn] = useState(initial.spentOn ?? today)
  const [endsOn, setEndsOn] = useState(initial.endsOn ?? '')
  const [note, setNote] = useState(initial.note ?? '')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && !busy && onClose()
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [busy, onClose])

  const amountKurus = liraToKurus(amount)
  const endsBad = kind === 'monthly' && !!endsOn && endsOn.slice(0, 7) <= spentOn.slice(0, 7)
  const valid = !!title.trim() && amountKurus != null && !!spentOn && !endsBad

  const submit = async () => {
    if (!valid || amountKurus == null) return
    setBusy(true)
    setError(null)
    try {
      await saveExpense({
        id: initial.id,
        title: title.trim(),
        category: category.trim(),
        amountKurus,
        kind,
        spentOn,
        endsOn: kind === 'monthly' && endsOn ? endsOn : null,
        note: note.trim(),
      })
      await onSaved()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Kaydedilemedi.')
      setBusy(false)
    }
  }

  return (
    <div className="dlg" role="dialog" aria-modal="true" aria-labelledby="exp-title" onClick={() => !busy && onClose()}>
      <div className="dlg__card dlg__card--x subs__dlg" onClick={(e) => e.stopPropagation()}>
        <button type="button" className="dlg__x" onClick={onClose} aria-label="Kapat" disabled={busy}>
          <XIcon />
        </button>
        <h2 id="exp-title">{initial.id ? 'Gideri düzenle' : 'Gider ekle'}</h2>

        <div className="plan-seg fin__kind" role="radiogroup" aria-label="Gider türü">
          {(
            [
              ['once', 'Tek seferlik', 'Reklam, cihaz…'],
              ['monthly', 'Her ay', 'Sunucu, mağaza hesabı…'],
            ] as const
          ).map(([id, label, hint]) => (
            <button
              key={id}
              type="button"
              role="radio"
              aria-checked={kind === id}
              className={kind === id ? 'is-on' : ''}
              onClick={() => setKind(id)}
            >
              {label}
              <small>{hint}</small>
            </button>
          ))}
        </div>

        <div className="subs__grid">
          <label className="dlgfield fin__wide">
            <span>Gider adı</span>
            <input value={title} onChange={(e) => setTitle(e.target.value)} maxLength={120} placeholder="Örn. Supabase" />
          </label>
          <label className="dlgfield">
            <span>{kind === 'monthly' ? 'Aylık tutar (₺)' : 'Tutar (₺)'}</span>
            <input value={amount} inputMode="decimal" onChange={(e) => setAmount(amountInput(e.target.value))} />
          </label>
          <label className="dlgfield">
            <span>Kategori</span>
            <input value={category} onChange={(e) => setCategory(e.target.value)} maxLength={60} placeholder="İsteğe bağlı" />
          </label>
          <div className="subs__field">
            <b>{kind === 'monthly' ? 'Başladığı gün' : 'Tarih'}</b>
            <DateField label={kind === 'monthly' ? 'Başladığı gün' : 'Tarih'} value={spentOn} onChange={setSpentOn} />
          </div>
          {kind === 'monthly' && (
            <div className="subs__field">
              <b>Bittiği gün</b>
              <DateField label="Bittiği gün" value={endsOn} onChange={setEndsOn} placeholder="Sürüyor" />
            </div>
          )}
        </div>

        {endsBad && <p className="superadmin__error">Bitiş ayı başlangıç ayından sonra olmalı.</p>}

        <label className="dlgfield">
          <span>Not</span>
          <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="İsteğe bağlı" maxLength={500} />
        </label>

        {error && <p className="superadmin__error">{error}</p>}

        <div className="dlg__buttons dlg__buttons--even">
          <button type="button" className="btn btn--ghost" onClick={onClose} disabled={busy}>
            Vazgeç
          </button>
          <button type="button" className="btn btn--primary" onClick={() => void submit()} disabled={busy || !valid}>
            {busy ? 'Kaydediliyor…' : 'Kaydet'}
          </button>
        </div>
      </div>
    </div>
  )
}
